// @ts-nocheck
"use client"

import type React from "react"

import { useState } from "react"
import { AlertTriangle } from "lucide-react"
import _ from 'lodash';

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { isSlugBlacklisted } from '@staty/shared/blacklistedSlugs'
import useOrganizationStore from '@store/organizationStore';
import { useAuth, useUser, useOrganizationList } from '@/auth/clerkShim';
import { useNavigate } from "react-router-dom";
import { TENANT_ROOT_DOMAIN } from '@staty/shared/domains';

export default function CreateLeagueModal({ league }) {
  const [open, setOpen] = useState(false)
  const [useSameStripeAccount, setUseSameStripeAccount] = useState(false)
  const [leagueName, setLeagueName] = useState("");
  const [leagueNameError, setLeagueNameError] = useState("");
  const [slugName, setSlugName] = useState("");
  const { getToken } = useAuth();
  const { user } = useUser();
  const { setActive } = useOrganizationList();
  const navigate = useNavigate();

  const { getOrganizationByName, getOrganizationByDisplayName, createOrganization } = useOrganizationStore((state) => ({
    getOrganizationByName: state.getOrganizationByName,
    getOrganizationByDisplayName: state.getOrganizationByDisplayName,
    // organizations: state.organizations,
    createOrganization: state.createOrganization,
  }));

  console.log('CreateLeagueModal::league::', league);

  const hasStripeAccount = league.stepsTaken && league.stepsTaken.createdStripeAccount;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // Handle form submission here
    console.log("Creating league:", { leagueName, useSameStripeAccount })
    const token = await getToken();
    getOrganizationByName(slugName, (res) => {
      if (res) {
        setLeagueNameError("This URL is already taken");
      } else {
        createOrganization(leagueName, slugName, user.id, token, league._id, async (res, err) => {
          if (err || !res) {
            setLeagueNameError(err || "Failed to create league.");
            return;
          }
          // The API returns { organization, admin } — the new org's id is the
          // reliable source. `admin.provider.organization` is a Clerk-era field
          // that is empty for Mongo-JWT admins; reading it alone sent
          // switch-org an undefined id → 403 "Not a member of that organization".
          const newOrg = _.get(res, 'organization._id') || _.get(res, 'admin.provider.organization');
          if (newOrg) {
            // MUST await: setActive POSTs /admins/auth/switch-org, and the page
            // we navigate to re-reads /admins/auth/me on mount — navigating
            // first races that read against the switch.
            try {
              await setActive({ organization: newOrg });
            } catch (e) {
              // Non-fatal: the org exists either way, so let the user through.
              console.error('Failed to activate the new organization', e);
            }
          }
          // INT-0050: no design step; the finish screen is the wizard's last line.
          navigate('/create/done');
        });
      }
    });
    setOpen(false)
    // Reset form
    setLeagueName("")
    setSlugName("")
    setUseSameStripeAccount(false)
  }

  const handleLeagueNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('handleLeagueNameChange::', e.target.value)
    setLeagueName(e.target.value);
    const slug = e.target.value.toLowerCase().replace(/\s/g, '-');
    if (!isSlugBlacklisted(slug)) { 
      setLeagueNameError("");
      setSlugName(slug);
    } else {
      setLeagueNameError("This URL is not allowed");
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {/* <Button>Create New League</Button> */}
        <span>Create a new league</span>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create New League</DialogTitle>
          <DialogDescription>Set up a new league with your preferred settings.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="league-name">League Name</Label>
              <Input
                id="league-name"
                value={leagueName}
                onChange={(e) => handleLeagueNameChange(e)}
                placeholder="Enter league name"
                required
              />
              <p className="mt-2 text-sm text-destructive">{leagueNameError}</p>
            </div>

            <div>
              <Label className="block text-sm font-medium leading-6 text-foreground" htmlFor="leagueSlug">
                League URL
              </Label>
              <div className="mt-2 flex rounded-md shadow-sm">
                <span className="inline-flex items-center rounded-l-md border border-r-0 border-input bg-muted/50 px-3 text-sm text-muted-foreground">
                  https://
                </span>
                <Input
                  className="block w-full flex-1 rounded-none rounded-r-md border-input focus:border-ring focus:ring-ring sm:text-sm"
                  id="leagueSlug"
                  name="leagueSlug"
                  value={slugName}
                  onChange={e => setSlugName(e.target.value)}
                  placeholder="your-league-name"
                  type="text"
                />
                <span className="inline-flex items-center rounded-r-md border border-l-0 border-input bg-muted/50 px-3 text-sm text-muted-foreground">
                  .{TENANT_ROOT_DOMAIN}
                </span>
              </div>
              {/* <p className="mt-2 text-sm text-red-500 dark:text-red-400">{formData.leagueUrl.errorMsg}</p> */}
              <p className="mt-2 text-sm text-muted-foreground">
                This is the URL where your league will be hosted. You can edit it if needed.
              </p>
            </div>

            {hasStripeAccount && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="stripe-account"
                checked={useSameStripeAccount}
                onCheckedChange={(checked) => setUseSameStripeAccount(checked as boolean)}
              />
              <Label
                htmlFor="stripe-account"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Use Same Payout Account
              </Label>
            </div>
            )}

            {useSameStripeAccount && (
              <Alert className="border-warning/30 bg-warning/10">
                <AlertTriangle className="h-4 w-4 text-warning" />
                <AlertDescription className="text-warning">
                  <strong>Warning:</strong> The payout account from {league.name} will be used for this new league. All payments and
                  transactions will be processed through this existing payout account.
                </AlertDescription>
              </Alert>
            )}
            <br />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!leagueName.trim() || !slugName.trim() || !!leagueNameError || leagueName.length < 4}>
              Create League
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
